import type { SimulationFilterEditedAction } from 'types/ppaSimulationMeta';
import type { SimulatorAppliedFilters, SimulatorFilterCategory, SimulatorFilterOption } from './SimulatorFilterPanel';

export const DEFAULT_SIMULATOR_TABLE_DIMENSIONS = ['dim1', 'dim2'] as const;

export type SimulatorFilterScopeItem = {
    dimension?: string;
    attribute_type?: string;
    attribute_ex_id?: string[];
};

export type SimulatorShowOnlyFilterItem = {
    id: string;
    input_value: string;
};

export type SimulatorTableRequestPayload = {
    scenario_id: number;
    scenario_tab_id: number | string;
    filters: Record<string, number[]>;
    user_input: SimulatorShowOnlyFilterItem[];
};

export type SimulatorShowOnlyState = {
    edited: boolean;
    impacted: boolean;
    editedFilters: SimulatorShowOnlyFilterItem[];
};

export type GlobalFilterInclusionItem = {
    attributeType: string;
    attributeDesc: string;
    attributeExId: string;
    dimension: string;
};

export const SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT = 50;
export const SIMULATOR_FILTER_FEATURE_NAME = 'default';

export const DEFAULT_SIMULATOR_FILTER_OPTIONS: SimulatorFilterOption[] = Array.from(
    { length: 12 },
    (_, index) => ({
        id: `option-${index + 1}`,
        label: 'Brand Name',
    }),
);

/** Meta id (e.g. sub_category) → global filter attributeType (e.g. Sub_Category). */
export function toSimulatorFilterAttributeType(id: string): string {
    return id
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('_');
}

function normalizeAttributeType(value: string): string {
    return value.trim().toLowerCase().replace(/[\s_]+/g, '_');
}

/** Maps scenario scope into global-filter `filters.inclusion` (excludes the active attribute). */
export function buildSimulatorFilterAttributeNamesInclusion(
    scope: SimulatorFilterScopeItem[] | undefined,
    currentAttributeType: string,
): GlobalFilterInclusionItem[] {
    if (!scope?.length || !currentAttributeType) return [];

    const normalizedCurrent = normalizeAttributeType(currentAttributeType);
    const inclusion: GlobalFilterInclusionItem[] = [];

    for (const item of scope) {
        if (!item.attribute_type || normalizeAttributeType(item.attribute_type) === normalizedCurrent) {
            continue;
        }

        const ids = item.attribute_ex_id;
        if (!Array.isArray(ids) || ids.length === 0) continue;

        const dimension = item.dimension ?? 'dim1';
        for (const exId of ids) {
            const attributeExId = String(exId);
            inclusion.push({
                attributeType: item.attribute_type,
                attributeDesc: attributeExId,
                attributeExId,
                dimension,
            });
        }
    }

    return inclusion;
}

export function toSimulatorShowOnlyFilterItems(
    actionTypes: SimulationFilterEditedAction[],
    selectedInputValues: string[],
): SimulatorShowOnlyFilterItem[] {
    const selected = new Set(selectedInputValues);
    return actionTypes
        .filter((action) => selected.has(action.input_value))
        .map(({ id, input_value }) => ({ id, input_value }));
}

/** Builds the `ppa/simulator/table` request body. `resolvedFilters` contains numeric dim ids from applyFilter. */
export function buildSimulatorTableRequestPayload({
    scenarioId,
    scenarioTabId,
    resolvedFilters,
    appliedFilters,
}: {
    scenarioId: number;
    scenarioTabId: number | string;
    resolvedFilters: Record<string, number[]>;
    appliedFilters: SimulatorAppliedFilters;
}): SimulatorTableRequestPayload {
    return {
        scenario_id: scenarioId,
        scenario_tab_id: scenarioTabId,
        filters: resolvedFilters,
        user_input: buildSimulatorFilterShowOnlyPayload(appliedFilters.showOnly) ?? [],
    };
}

/** API `show_only` payload — `{ id, input_value }[]` for active toggles / selected action types. */
export function buildSimulatorFilterShowOnlyPayload(
    showOnly: SimulatorShowOnlyState,
): SimulatorShowOnlyFilterItem[] | undefined {
    const payload: SimulatorShowOnlyFilterItem[] = [];

    if (showOnly.edited) {
        if (showOnly.editedFilters.length > 0) {
            payload.push(...showOnly.editedFilters);
        } else {
            payload.push({ id: 'edited', input_value: 'edited' });
        }
    }

    if (showOnly.impacted) {
        payload.push({ id: 'impacted', input_value: 'impacted' });
    }

    return payload.length > 0 ? payload : undefined;
}

export const EMPTY_SIMULATOR_SHOW_ONLY_STATE: SimulatorShowOnlyState = {
    edited: false,
    impacted: false,
    editedFilters: [],
};

export const getSimulatorFilterOptionLabel = (
    category: SimulatorFilterCategory,
    optionId: string,
): string => {
    const options = category.options ?? DEFAULT_SIMULATOR_FILTER_OPTIONS;
    return options.find((option) => option.id === optionId)?.label ?? optionId;
};

export type SimulatorFilterDetailGroup = {
    key: string;
    label: string;
    items: { id: string; label: string }[];
};

export const buildSimulatorFilterDetailGroups = (
    filterCategories: SimulatorFilterCategory[],
    categorySelections: Record<string, string[]>,
    showOnly: SimulatorShowOnlyState,
    editedActionTypes: SimulationFilterEditedAction[] = [],
    options: { includeShowOnly?: boolean } = {},
): SimulatorFilterDetailGroup[] => {
    const { includeShowOnly = true } = options;

    const categoryGroups = filterCategories
        .map((category) => {
            const selectedIds = categorySelections[category.key] ?? [];
            if (selectedIds.length === 0) return null;
            return {
                key: category.key,
                label: category.label,
                items: selectedIds.map((id) => ({
                    id,
                    label: getSimulatorFilterOptionLabel(category, id),
                })),
            };
        })
        .filter((group): group is SimulatorFilterDetailGroup => group !== null);

    if (!includeShowOnly) {
        return categoryGroups;
    }

    const editedItems =
        showOnly.edited && showOnly.editedFilters.length > 0
            ? showOnly.editedFilters.map((filter) => {
                  const label =
                      editedActionTypes.find((action) => action.input_value === filter.input_value)?.label ??
                      filter.input_value;
                  return { id: filter.input_value, label };
              })
            : showOnly.edited
              ? [{ id: 'show_only_edited', label: 'Edited' }]
              : [];

    return [
        ...categoryGroups,
        ...(editedItems.length > 0
            ? [{ key: 'show_only_edited', label: 'Edited', items: editedItems }]
            : []),
        ...(showOnly.impacted
            ? [{ key: 'show_only_impacted', label: 'Impacted', items: [{ id: 'show_only_impacted', label: 'Impacted' }] }]
            : []),
    ];
};
